/**
 * Customer-mode packaging flag (Sprint 115, ADR-108).
 *
 * CondoManager (CondoPro) ships as one tile inside a multi-app demo monorepo.
 * `/` redirects to `/tileapps` — a launcher grid of UNRELATED demo apps
 * (tenant-management, med-tec, fin-auditor, scheduler, payment-flow, plus a
 * duplicate "tenant-management" surface). That is fine for internal demos but
 * launch-blocking for a real customer build: a buyer must land directly in
 * CondoPro and must NOT see sibling demo apps.
 *
 * This flag flips the build between the two modes. It is a BUILD-TIME / rollout
 * switch (read from `process.env`), NOT a per-condo licensed capability, so per
 * `docs/architecture/feature-flag-conventions.md` ("What flags are NOT for") it
 * does NOT belong in the `condo.condo_corp_features` table — same reasoning as
 * the `atriumRedesign` client toggle.
 *
 *   REACT_APP_CUSTOMER_MODE
 *     unset / 'false' / '0'  → DEMO mode (DEFAULT). Current dev behavior is
 *                              UNCHANGED: `/` → `/tileapps`, all demo tiles
 *                              visible & reachable. Existing dev workflows are
 *                              not surprised.
 *     'true' / '1'           → CUSTOMER mode. `/` → `/condo-app`, the demo
 *                              launcher tiles are hidden, and the demo-app routes
 *                              (incl. the duplicate tenant-management surface)
 *                              are not surfaced as navigable tiles.
 *
 * HOW A CUSTOMER BUILD FLIPS IT: set `REACT_APP_CUSTOMER_MODE=true` in the
 * build environment (e.g. the customer-domain CI/CD `.env` or the Docker build
 * arg) before `npm run build`. CRA inlines `REACT_APP_*` at build time.
 *
 * NON-DESTRUCTIVE: the demo apps and their routes are NOT deleted — customer
 * mode only changes the landing redirect and hides the launcher tiles, so the
 * demo monorepo remains fully intact and the switch is reversible.
 */
export function isCustomerMode(): boolean {
  const v = (process.env.REACT_APP_CUSTOMER_MODE || '').trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'on' || v === 'yes';
}

/** The path `/` (and the catch-all) should land on, given the active mode. */
export function defaultLandingPath(): string {
  return isCustomerMode() ? '/condo-app/' : '/tileapps';
}
